/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import ReactPlayer from 'react-player';
import ForNoImage from 'Assets/Placeholder.png';
import {
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Collapse,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@material-ui/core';
import {
  Close,
  CloudUpload,
  VideoLibrary,
  Public,
  Lock,
  Group,
  CheckCircle,
  ExpandMore,
  ExpandLess,
  PlayArrow,
  Edit,
  Description,
  Category,
  PlaylistPlay,
  Visibility,
  Save,
  GetApp,
  Add,
} from '@material-ui/icons';
import { endpoints, dataFetcher, axios, baseUrl } from 'Api';
import CreateCategoryPage from 'Pages/private/content-layout/CreateCategory';
import { AppContext } from 'Context';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Pathname } from 'Routes';
import { getVideosDetail } from '../../Api/apiFunctions';

const VideoEditModal = ({
  open,
  onClose,
  videoId = null,
  onSaveSuccess = null,
  onDownloadVideos = null,
}) => {
  const {
    userDetails,
    setAppSnackbar,
    autoSignIn,
    defaultCategoryName,
    setShowAddPlaylistModal,
    playlistRefreshTrigger,
    newlyCreatedPlaylistId,
    setNewlyCreatedPlaylistId,
    setShowAddSubCategoryModal,
    subCategoryRefreshTrigger,
    newlyCreatedSubCategoryId,
    setNewlyCreatedSubCategoryId,
    downloadingFiles,
  } = useContext(AppContext);

  const userId = userDetails?.isSubscription?.user_id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isFromGift = searchParams.get('fromGift') === 'true';

  // Single video states
  const [videoData, setVideoData] = useState(null);
  console.log('videoData', videoData);

  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('2');
  const [selectedCategory, setSelectedCategory] = useState({
    value: '',
    name: '',
  });
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedPlaylists, setSelectedPlaylists] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [thumbnails, setThumbnails] = useState([]);
  const [selectedThumbnail, setSelectedThumbnail] = useState(0);
  const [customThumbnail, setCustomThumbnail] = useState(null);
  const [isCustomThumbnailSelected, setIsCustomThumbnailSelected] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const customThumbnailInputRef = useRef(null);
  const [showAdditionalFields, setShowAdditionalFields] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoSize, setVideoSize] = useState(0);
  const [isDownloadDisabled, setIsDownloadDisabled] = useState(true);
  const isAlreadyDownloading = downloadingFiles?.some(
    (f) => ((f.videoId || f.id) === videoData?.content_id || (f.videoId || f.id) === videoId)
      && (f.status === 'downloading' || f.status === 'pending')
  );
  const additionalOptionsRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);

  // Video URL fallback logic (same as video-player-0)
  const [currentVideoUrl, setCurrentVideoUrl] = useState(null);
  const [hasTriedFallback, setHasTriedFallback] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const loadTimeoutRef = useRef(null);

  // Timeout-based fallback: if video doesn't play within timeout, switch to video_raw_file
  useEffect(() => {
    if (currentVideoUrl && !hasTriedFallback) {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
      const isHlsStream = currentVideoUrl?.includes('.m3u8');
      const timeoutDuration = isHlsStream ? 10000 : 6000;
      loadTimeoutRef.current = setTimeout(() => {
        if (!hasTriedFallback && videoData?.video_raw_file) {
          setCurrentVideoUrl(videoData.video_raw_file);
          setHasTriedFallback(true);
        } else if (!hasTriedFallback) {
          setVideoFailed(true);
        }
      }, timeoutDuration);
    }
    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
  }, [currentVideoUrl, hasTriedFallback, videoData]);

  // Handle video error - fallback to video_raw_file
  const handleVideoError = useCallback((error) => {
    if (error?.name === 'NotAllowedError' || (error?.message && error.message.includes('interact'))) {
      return;
    }
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
    if (!hasTriedFallback && videoData?.video_raw_file) {
      setCurrentVideoUrl(videoData.video_raw_file);
      setHasTriedFallback(true);
    } else {
      setVideoFailed(true);
    }
  }, [hasTriedFallback, videoData]);

  // Clear video URL state on video ready (cancel timeout)
  const handleVideoReady = useCallback(() => {
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
  }, []);

  // Fetch video details when modal opens
  useEffect(() => {
    const fetchVideoDetails = async () => {
      if (!open || !videoId) return;

      setIsLoading(true);
      setIsDownloadDisabled(true);
      try {
        const response = await getVideosDetail({ id: videoId });
        if (response?.data?.success && response?.data?.data) {
          const videoDetails = response.data.data;
          console.log('videoDetails', videoDetails);
          setVideoData(videoDetails);

          // Set video preview URL with priority: transcoded_url > video_file > mp4_url > video_raw_file
          const primaryUrl = videoDetails?.transcoded_url || videoDetails?.video_file || videoDetails?.mp4_url || videoDetails?.video_raw_file;
          setCurrentVideoUrl(primaryUrl);
          setHasTriedFallback(false);
          setVideoFailed(false);

          // Check download availability - verify the file actually exists on S3
          const rawFileUrl = videoDetails?.video_raw_file;
          if (rawFileUrl && rawFileUrl.trim() !== '') {
            try {
              // Range request with just 1 byte (fastest way to check file exists)
              // fileCheckResponse = await fetch(rawFileUrl, { method: 'HEAD' });
              const fileCheckResponse = await fetch(rawFileUrl, {
                method: 'GET',
                headers: { Range: 'bytes=0-0' },
              });

              // Check content-type - S3 errors return application/xml
              const contentType = fileCheckResponse.headers.get('content-type') || '';
              const isXmlError = contentType.includes('xml');

              setIsDownloadDisabled(!fileCheckResponse.ok || isXmlError);
            } catch {
              setIsDownloadDisabled(true);
            }
          } else {
            setIsDownloadDisabled(true);
          }

          // Populate form fields
          setTitle(isFromGift ? '' : (videoDetails.title || ''));
          setDescription(videoDetails.description || '');
          setVisibility(videoDetails.visibility || '2');
          setVideoDuration(videoDetails.video_duration || 0);
          setVideoSize(videoDetails.video_size || 0);

          // Set category
          if (videoDetails.category_id) {
            setSelectedCategory({
              value: videoDetails.category_id,
              name: videoDetails.category_title || '',
            });
          }

          // Set subcategory
          setSelectedSubCategory(videoDetails.subcategory_id || '');

          // Set playlists - handle both string and array formats, convert to numbers
          if (videoDetails.playlists) {
            try {
              const playlistIds = typeof videoDetails.playlists === 'string'
                ? JSON.parse(videoDetails.playlists)
                : videoDetails.playlists;
              const rawIds = Array.isArray(playlistIds) ? playlistIds : [playlistIds];
              setSelectedPlaylists(rawIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id)));
            } catch (e) {
              // If it's a single value, wrap in array
              const id = Number(videoDetails.playlists);
              setSelectedPlaylists(Number.isNaN(id) ? [] : [id]);
            }
          } else if (videoDetails.playlist_ids) {
            try {
              let playlistIds;
              if (typeof videoDetails.playlist_ids === 'string') {
                if (videoDetails.playlist_ids.trim().startsWith('[')) {
                  playlistIds = JSON.parse(videoDetails.playlist_ids);
                } else {
                  playlistIds = videoDetails.playlist_ids.split(',').map((id) => Number(id.trim()));
                }
              } else {
                playlistIds = videoDetails.playlist_ids;
              }
              const rawIds = Array.isArray(playlistIds) ? playlistIds : [playlistIds];
              setSelectedPlaylists(rawIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id)));
            } catch (e) {
              setSelectedPlaylists([]);
            }
          } else if (videoDetails.playlist_id) {
            const id = Number(videoDetails.playlist_id);
            setSelectedPlaylists(Number.isNaN(id) ? [] : [id]);
          }

          // Set groups - handle both string and array formats
          if (videoDetails.group_id) {
            try {
              const groupIds = typeof videoDetails.group_id === 'string'
                ? JSON.parse(videoDetails.group_id)
                : videoDetails.group_id;
              setSelectedGroups(Array.isArray(groupIds) ? groupIds : []);
            } catch (e) {
              setSelectedGroups([]);
            }
          }

          // Set thumbnails from API response
          if (videoDetails.thumbnail_list) {
            try {
              // Parse thumbnail_list if it's a JSON string
              const thumbnailArray = typeof videoDetails.thumbnail_list === 'string'
                ? JSON.parse(videoDetails.thumbnail_list)
                : videoDetails.thumbnail_list;

              if (Array.isArray(thumbnailArray) && thumbnailArray.length > 0) {
                setThumbnails(thumbnailArray);
                const currentThumbIndex = thumbnailArray.indexOf(videoDetails?.thumbnails[0]);
                console.log('thumbnailArray', thumbnailArray, currentThumbIndex);
                setSelectedThumbnail(currentThumbIndex !== -1 ? currentThumbIndex : 0);
              } else if (videoDetails.thumbnail) {
                setThumbnails([videoDetails.thumbnail]);
                setSelectedThumbnail(0);
              }
            } catch (e) {
              console.error('Error parsing thumbnail_list:', e);
              // Fallback to single thumbnail if parsing fails
              if (videoDetails.thumbnail) {
                setThumbnails([videoDetails.thumbnail]);
                setSelectedThumbnail(0);
              }
            }
          } else if (videoDetails.thumbnail) {
            setThumbnails([videoDetails.thumbnail]);
            setSelectedThumbnail(0);
          }
        } else {
          setAppSnackbar({
            open: true,
            message: 'Failed to load video details',
            severity: 'error',
          });
        }
      } catch (error) {
        console.error('Error fetching video details:', error);
        setAppSnackbar({
          open: true,
          message: 'Error loading video details',
          severity: 'error',
        });
        setIsDownloadDisabled(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVideoDetails();
  }, [open, videoId]);

  // Fetch categories, subcategories, playlists, groups
  useEffect(() => {
    const fetchAllData = async () => {
      if (!open) return;

      // Reset loaded states
      setGroupsLoaded(false);

      try {
        // Fetch categories
        const categoriesResponse = await dataFetcher(endpoints.getMyCategories);
        if (categoriesResponse.success && categoriesResponse.data) {
          // Sort categories to always show "All Videos" at the top
          const sortedCategories = [...categoriesResponse.data].sort((a, b) => {
            const aIsAllVideos = a.title?.toLowerCase() === 'all videos';
            const bIsAllVideos = b.title?.toLowerCase() === 'all videos';
            if (aIsAllVideos && !bIsAllVideos) return -1;
            if (!aIsAllVideos && bIsAllVideos) return 1;
            return 0; // Keep original order for other categories
          });
          setCategories(sortedCategories);
        }

        // Fetch subcategories
        const subCategoriesResponse = await dataFetcher(endpoints.getMySubCategory);
        if (subCategoriesResponse.success && subCategoriesResponse.data) {
          setSubCategories(subCategoriesResponse.data);
        }

        // Fetch playlists
        const playlistsResponse = await dataFetcher(endpoints.getMyPlaylist);
        if (playlistsResponse.success && playlistsResponse.data) {
          setPlaylists(playlistsResponse.data);
        }

        // Fetch groups
        const groupsResponse = await dataFetcher(endpoints.getMyGroups);
        if (groupsResponse.success && groupsResponse.data) {
          setGroups(groupsResponse.data);
        } else if (!groupsResponse.success && groupsResponse.message === 'No Groups found') {
          setGroups([]);
        }
        setGroupsLoaded(true);
      } catch (error) {
        console.error('Error fetching data:', error);
        setGroups([]);
        setGroupsLoaded(true);
      }
    };

    fetchAllData();
  }, [open]);

  // Refresh playlists when a new playlist is created from the global modal
  useEffect(() => {
    if (playlistRefreshTrigger > 0 && open) {
      const refreshPlaylists = async () => {
        try {
          const playlistsResponse = await dataFetcher(endpoints.getMyPlaylist);
          if (playlistsResponse.success && playlistsResponse.data) {
            setPlaylists(playlistsResponse.data);
            // Auto-select the newly created playlist by finding it in fetched data
            if (newlyCreatedPlaylistId) {
              const newPlaylist = playlistsResponse.data.find(
                (p) => String(p.id) === String(newlyCreatedPlaylistId)
              );
              if (newPlaylist && !selectedPlaylists.includes(newPlaylist.id)) {
                setSelectedPlaylists((prev) => [...prev, newPlaylist.id]);
              }
              setNewlyCreatedPlaylistId(null);
            }
          }
        } catch (error) {
          console.error('Error refreshing playlists:', error);
        }
      };
      refreshPlaylists();
    }
  }, [playlistRefreshTrigger, open]);

  // Refresh subcategories when a new subcategory is created from the global modal
  useEffect(() => {
    if (subCategoryRefreshTrigger > 0 && open) {
      const refreshSubCategories = async () => {
        try {
          const subCategoriesResponse = await dataFetcher(endpoints.getMySubCategory);
          if (subCategoriesResponse.success && subCategoriesResponse.data) {
            setSubCategories(subCategoriesResponse.data);
            // Auto-select the newly created subcategory
            if (newlyCreatedSubCategoryId) {
              setSelectedSubCategory(newlyCreatedSubCategoryId);
              setNewlyCreatedSubCategoryId(null);
            }
          }
        } catch (error) {
          console.error('Error refreshing subcategories:', error);
        }
      };
      refreshSubCategories();
    }
  }, [subCategoryRefreshTrigger, open]);

  // Handle category creation callback - refresh list and select new category
  const handleCategoryCreated = async () => {
    try {
      const categoriesResponse = await dataFetcher(endpoints.getMyCategories);
      if (categoriesResponse.success && categoriesResponse.data) {
        // Sort categories to always show "All Videos" at the top
        const sortedCategoriesForDisplay = [...categoriesResponse.data].sort((a, b) => {
          const aIsAllVideos = a.title?.toLowerCase() === 'all videos';
          const bIsAllVideos = b.title?.toLowerCase() === 'all videos';
          if (aIsAllVideos && !bIsAllVideos) return -1;
          if (!aIsAllVideos && bIsAllVideos) return 1;
          return 0; // Keep original order for other categories
        });
        setCategories(sortedCategoriesForDisplay);
        // Select the most recently created category (last in the list by id)
        const sortedCategories = [...categoriesResponse.data].sort((a, b) => b.id - a.id);
        if (sortedCategories.length > 0) {
          const newestCategory = sortedCategories[0];
          setSelectedCategory({
            value: newestCategory.id,
            name: newestCategory.title,
          });
        }
      }
    } catch (error) {
      console.error('Error refreshing categories:', error);
    } finally {
      setShowCreateCategoryModal(false);
    }
  };

  const handleCustomThumbnailUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setAppSnackbar({
        open: true,
        message: 'Please upload a valid image file (JPG, PNG, or WebP)',
        severity: 'error',
      });
      return;
    }

    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setCustomThumbnail(e.target.result);
      setIsCustomThumbnailSelected(true);
      setSelectedThumbnail(null);
      setIsVideoPlaying(false);
    };
    reader.readAsDataURL(file);
  };

  const handleClose = () => {
    // Reset state
    setVideoData(null);
    setTitle('');
    setDescription('');
    setVisibility('2');
    setSelectedCategory({ value: '', name: '' });
    setSelectedSubCategory('');
    setSelectedPlaylists([]);
    setSelectedGroups([]);
    setThumbnails([]);
    setSelectedThumbnail(0);
    setCustomThumbnail(null);
    setIsCustomThumbnailSelected(false);
    setIsVideoPlaying(false);
    setShowAdditionalFields(true);
    setIsSaving(false);
    setIsLoading(false);
    setVideoDuration(0);
    setVideoSize(0);
    setIsDownloadDisabled(true);

    onClose();
  };

  const handleDownload = () => {
    if (!onDownloadVideos || !videoData) {
      setAppSnackbar({
        open: true,
        message: 'Video file not available',
        severity: 'error',
      });
      return;
    }

    // Route through the shared downloadSelectedVideos function
    onDownloadVideos([{
      content_id: videoData.content_id || videoId,
      video_id: videoData.video_id || videoId,
      title: videoData.title || 'video',
      extension: videoData.extension || 'mp4',
      video_file: videoData.video_file,
    }]);
  };

  const handleSave = async () => {
    if (!videoData) return;

    // Validate group selection if visibility is Group
    if (
      visibility === '3'
      && (!selectedGroups || selectedGroups.length === 0)
    ) {
      alert(
        'Please select at least one group when Group visibility is selected.'
      );
      return;
    }

    setIsSaving(true);

    try {
      const apiUrl = `${baseUrl}${endpoints.addVideoContent}`;
      const formData = new FormData();

      formData.append('video_id', videoId);
      formData.append('draft_id', videoData.draft_id || '');
      formData.append('title', title || '');
      formData.append('category_id', selectedCategory.value || '');

      // Handle description - use 'delete' if empty
      if (description && description.trim()) {
        formData.append('description', description);
      } else {
        formData.append('description', 'delete');
      }

      formData.append('visibility', visibility || '2');
      formData.append('status', 1);

      // Add subcategory if selected
      if (selectedSubCategory) {
        formData.append('subcategory_id', selectedSubCategory);
      }

      // Add playlists if selected (send as comma-separated like groups)
      if (selectedPlaylists && selectedPlaylists.length > 0) {
        formData.append('playlist_id', selectedPlaylists.join(','));
      }

      // Add group_id as comma-separated string if visibility is Group
      if (visibility === '3' && selectedGroups && selectedGroups.length > 0) {
        formData.append('group_id', selectedGroups.join(','));
      }

      // Handle thumbnail_list - send comma-separated URLs
      if (thumbnails && thumbnails.length > 0) {
        formData.append('thumbnail_list', thumbnails.join(','));
      }

      // Handle thumbnail selection
      if (isCustomThumbnailSelected && customThumbnail) {
        // For custom thumbnail, send as base64
        formData.append('custom_thumbnails', customThumbnail.split('base64,')[1]);
      } else if (thumbnails[selectedThumbnail]) {
        // If user selected an existing thumbnail, send its URL
        formData.append('thumbnails', thumbnails[selectedThumbnail]);
      }

      const response = await axios.post(apiUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response?.data?.success) {
        setAppSnackbar({
          open: true,
          message: 'Video updated successfully',
          severity: 'success',
        });

        if (onSaveSuccess) {
          onSaveSuccess(response.data);
        }

        handleClose();
      } else {
        setAppSnackbar({
          open: true,
          message: response?.data?.message || 'Failed to update video',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Error updating video:', error);
      setAppSnackbar({
        open: true,
        message: 'Error updating video',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 MB';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round((bytes / 1024 ** i) * 100) / 100} ${sizes[i]}`;
  };

  const handleAdditionalOptionsToggle = () => {
    const newState = !showAdditionalFields;
    setShowAdditionalFields(newState);

    // If expanding, scroll to the additional options section after a brief delay
    if (newState && additionalOptionsRef.current) {
      setTimeout(() => {
        additionalOptionsRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 150);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 20,
          maxWidth: 1100,
          maxHeight: '92vh',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        },
      }}
    >
      <DialogContent
        style={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 24px',
            backgroundColor: '#fff',
            borderBottom: '1px solid #e5e7eb',
            borderRadius: '20px 20px 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
              style={{
                backgroundColor: '#f3f4f6',
                borderRadius: '10px',
                padding: '8px',
                display: 'flex',
                marginRight: 12,
              }}
            >
              <Edit
                style={{ color: '#5aa5ef', fontSize: 24 }}
              />
            </div>
            <h2
              style={{
                margin: 0,
                color: '#1f2937',
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'Montserrat',
              }}
            >
              Edit Video Details
            </h2>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!isLoading && (
              <Button
                onClick={handleDownload}
                variant="contained"
                disabled={isDownloadDisabled || isAlreadyDownloading}
                title={isAlreadyDownloading ? 'Download in progress...' : isDownloadDisabled ? 'Download not available' : 'Download video'}
                style={{
                  backgroundColor: (isDownloadDisabled || isAlreadyDownloading) ? '#d1d5db' : '#5aa5ef',
                  color: 'white',
                  borderRadius: 8,
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: 'Montserrat',
                  boxShadow: 'none',
                  textTransform: 'none',
                  minWidth: 'auto',
                  cursor: (isDownloadDisabled || isAlreadyDownloading) ? 'not-allowed' : 'pointer',
                }}
              >
                <GetApp style={{ fontSize: 16, marginRight: 6 }} />
                {isAlreadyDownloading ? 'Downloading...' : 'Download'}
              </Button>
            )}
            <IconButton
              onClick={handleClose}
              style={{ color: '#6b7280' }}
            >
              <Close />
            </IconButton>
          </div>
        </div>

        {/* Content */}
        <div style={{ overflowY: 'auto', padding: '0 24px' }}>
          {isLoading ? (
            // Beautiful loader while fetching video details
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                height: '100%',
                minHeight: 450,
                padding: '40px 20px',
              }}
            >
              <div
                style={{
                  position: 'relative',
                  marginBottom: 32,
                }}
              >
                <CircularProgress
                  size={80}
                  thickness={3}
                  style={{ color: '#5aa5ef' }}
                />
                <VideoLibrary
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: 32,
                    color: '#5aa5ef',
                  }}
                />
              </div>
              <h3
                style={{
                  color: '#1A1A1A',
                  fontSize: 18,
                  fontWeight: 600,
                  margin: '0 0 8px 0',
                  fontFamily: 'Montserrat',
                  textAlign: 'center',
                }}
              >
                Loading video details...
              </h3>
              <p
                style={{
                  color: '#6B7280',
                  fontSize: 14,
                  margin: 0,
                  fontFamily: 'Montserrat',
                  textAlign: 'center',
                  maxWidth: 300,
                }}
              >
                Please wait while we fetch your video information
              </p>
            </div>
          ) : videoData ? (
            <div style={{ display: 'flex', height: 'calc(100% - 140px)', gap: 0 }}>
              {/* Left Side: Form Controls - 45% */}
              <div
                style={{
                  flex: '0 0 45%',
                  backgroundColor: '#fff',
                  padding: '28px 24px',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 18,
                  borderRight: '1px solid #e5e7eb',
                }}
              >
                {/* Video Title */}
                <div>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: 8,
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#1f2937',
                      fontFamily: 'Montserrat',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Video Title
                  </label>
                  <TextField
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter an engaging title..."
                    variant="outlined"
                    fullWidth
                    size="small"
                    InputProps={{
                      style: {
                        backgroundColor: '#fff',
                        borderRadius: 10,
                        fontFamily: 'Montserrat',
                        fontSize: 14,
                      },
                    }}
                  />
                </div>

                {/* Category & Visibility Row */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 16,
                  }}
                >
                  {/* Category */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: 8,
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#1f2937',
                        fontFamily: 'Montserrat',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Category
                    </label>
                    <FormControl fullWidth variant="outlined" size="small">
                      <Select
                        value={selectedCategory.value}
                        onChange={(e) => {
                          if (e.target.value === '__create_new__') {
                            setShowCreateCategoryModal(true);
                            return;
                          }
                          const selectedCat = categories?.find(
                            (cat) => cat?.id === e.target.value
                          );
                          setSelectedCategory({
                            value: e.target.value,
                            name: selectedCat?.title,
                          });
                        }}
                        style={{
                          backgroundColor: '#fff',
                          borderRadius: 10,
                          fontFamily: 'Montserrat',
                          fontSize: 13,
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                            },
                          },
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                          getContentAnchorEl: null,
                          autoFocus: false,
                          disableAutoFocusItem: true,
                        }}
                      >
                        <MenuItem value="__create_new__">
                          <div style={{ display: 'flex', alignItems: 'center', color: '#5aa5ef' }}>
                            <Add style={{ fontSize: 16, marginRight: 8, color: '#5aa5ef' }} />
                            <span style={{ fontSize: 13, fontFamily: 'Montserrat', fontWeight: 600 }}>Create New Category</span>
                          </div>
                        </MenuItem>
                        <Divider style={{ margin: '4px 0' }} />
                        {categories?.map((cat) => (
                          <MenuItem key={cat?.id} value={cat?.id}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <Category style={{ fontSize: 16, marginRight: 8, color: '#667eea' }} />
                              <span style={{ fontSize: 13, fontFamily: 'Montserrat' }}>{cat?.title}</span>
                            </div>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>

                  {/* Visibility */}
                  <div>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: 8,
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#1f2937',
                        fontFamily: 'Montserrat',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Visibility
                    </label>
                    <FormControl fullWidth variant="outlined" size="small">
                      <Select
                        value={visibility}
                        onChange={(e) => setVisibility(e.target.value)}
                        style={{
                          backgroundColor: '#fff',
                          borderRadius: 10,
                          fontFamily: 'Montserrat',
                          fontSize: 13,
                        }}
                      >
                        <MenuItem value="2">
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Public style={{ fontSize: 16, marginRight: 8, color: '#10B981' }} />
                            <span style={{ fontSize: 13, fontFamily: 'Montserrat' }}>All Viewers</span>
                          </div>
                        </MenuItem>
                        <MenuItem value="1">
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Lock style={{ fontSize: 16, marginRight: 8, color: '#EF4444' }} />
                            <span style={{ fontSize: 13, fontFamily: 'Montserrat' }}>Private</span>
                          </div>
                        </MenuItem>
                        <MenuItem value="3">
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Group style={{ fontSize: 16, marginRight: 8, color: '#8B5CF6' }} />
                            <span style={{ fontSize: 13, fontFamily: 'Montserrat' }}>Group</span>
                          </div>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                </div>

                {/* Group Selection (when visibility is Group) */}
                {visibility === '3' && (
                  <div>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: 8,
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#1f2937',
                        fontFamily: 'Montserrat',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Select Groups
                    </label>
                    <div
                      style={{
                        backgroundColor: '#fff',
                        borderRadius: 10,
                        padding: '8px',
                        maxHeight: '160px',
                        overflowY: 'auto',
                        border:
                          visibility === '3' && (!selectedGroups || selectedGroups.length === 0)
                            ? '2px solid #EF4444'
                            : '2px solid #e5e7eb',
                        boxShadow:
                          visibility === '3' && (!selectedGroups || selectedGroups.length === 0)
                            ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
                            : 'none',
                      }}
                    >
                      {!groupsLoaded ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '60px',
                          }}
                        >
                          <CircularProgress
                            size={16}
                            style={{ color: '#5aa5ef' }}
                          />
                          <span
                            style={{
                              marginLeft: '8px',
                              color: '#6B7280',
                              fontSize: '14px',
                              fontFamily: 'Montserrat',
                            }}
                          >
                            Loading groups...
                          </span>
                        </div>
                      ) : groups.length === 0 ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '60px',
                            backgroundColor:
                              visibility === '3' ? '#FEF2F2' : 'transparent',
                            border:
                              visibility === '3' ? '2px solid #EF4444' : 'none',
                            borderRadius: visibility === '3' ? '8px' : '0',
                            boxShadow:
                              visibility === '3'
                                ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
                                : 'none',
                          }}
                        >
                          <span
                            style={{
                              color: '#9CA3AF',
                              fontStyle: 'italic',
                              fontSize: '14px',
                              fontFamily: 'Montserrat',
                            }}
                          >
                            No groups available
                          </span>
                        </div>
                      ) : (
                        groups.map((group) => (
                          <FormControlLabel
                            key={group.id}
                            control={(
                              <Checkbox
                                checked={selectedGroups.includes(group.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedGroups([
                                      ...selectedGroups,
                                      group.id,
                                    ]);
                                  } else {
                                    setSelectedGroups(
                                      selectedGroups.filter(
                                        (id) => id !== group.id
                                      )
                                    );
                                  }
                                }}
                                style={{ color: '#8B5CF6' }}
                              />
                            )}
                            label={(
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Group style={{ fontSize: 15, marginRight: 8, color: '#8B5CF6' }} />
                                <span
                                  style={{
                                    fontFamily: 'Montserrat',
                                    fontSize: 13,
                                    color: '#1f2937',
                                    fontWeight: 500,
                                  }}
                                >
                                  {group.title}
                                </span>
                              </div>
                            )}
                            style={{
                              margin: '0',
                              padding: '6px 8px',
                              width: '100%',
                              borderRadius: '8px',
                              transition: 'background-color 0.2s ease',
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.backgroundColor = '#f3f4f6';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          />
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Additional Options - Combined Box */}
                <div
                  style={{
                    padding: 18,
                    backgroundColor: '#fff',
                    borderRadius: 10,
                    border: '2px solid #e5e7eb',
                  }}
                >
                  {/* Toggle Header */}
                  <div
                    ref={additionalOptionsRef}
                    onClick={handleAdditionalOptionsToggle}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      padding: '4px 0',
                      marginBottom: showAdditionalFields ? 16 : 0,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div
                        style={{
                          backgroundColor: '#f3f4f6',
                          borderRadius: '8px',
                          padding: '6px',
                          display: 'flex',
                          marginRight: 10,
                        }}
                      >
                        <Edit style={{ color: '#5aa5ef', fontSize: 16 }} />
                      </div>
                      <span
                        style={{
                          color: '#1f2937',
                          fontSize: 13,
                          fontWeight: 600,
                          fontFamily: 'Montserrat',
                        }}
                      >
                        Additional Options (Optional)
                      </span>
                    </div>
                    {showAdditionalFields ? (
                      <ExpandLess style={{ color: '#5aa5ef', fontSize: 22 }} />
                    ) : (
                      <ExpandMore style={{ color: '#5aa5ef', fontSize: 22 }} />
                    )}
                  </div>

                  {/* Additional Fields */}
                  <Collapse in={showAdditionalFields}>
                    <div>
                    {/* Description */}
                    <div style={{ marginBottom: 16 }}>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: 8,
                          fontSize: 13,
                          fontWeight: 700,
                          color: '#1f2937',
                          fontFamily: 'Montserrat',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Description
                      </label>
                      <TextField
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Add a compelling description for your video..."
                        variant="outlined"
                        fullWidth
                        multiline
                        minRows={3}
                        size="small"
                        InputProps={{
                          style: {
                            backgroundColor: '#fafbfc',
                            borderRadius: 10,
                            fontFamily: 'Montserrat',
                            fontSize: 13,
                          },
                        }}
                      />
                    </div>

                    {/* Subcategory and Playlist Grid */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 14,
                      }}
                    >
                      {/* Subcategory */}
                      <div>
                        <label
                          style={{
                            display: 'block',
                            marginBottom: 8,
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#1f2937',
                            fontFamily: 'Montserrat',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Subcategory
                        </label>
                        <FormControl fullWidth variant="outlined" size="small">
                          <Select
                            value={selectedSubCategory}
                            onChange={(e) => {
                              if (e.target.value === '__create_new__') {
                                setShowAddSubCategoryModal(true);
                                return;
                              }
                              setSelectedSubCategory(e.target.value);
                            }}
                            style={{
                              backgroundColor: '#fafbfc',
                              borderRadius: 10,
                              fontFamily: 'Montserrat',
                              fontSize: 13,
                            }}
                            MenuProps={{
                              variant: 'menu',
                              PaperProps: {
                                style: {
                                  maxHeight: 300,
                                },
                              },
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                              },
                              getContentAnchorEl: null,
                            }}
                          >
                            <MenuItem
                              value="__create_new__"
                              style={{
                                position: 'sticky',
                                top: 0,
                                backgroundColor: 'white',
                                zIndex: 1,
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', color: '#5aa5ef' }}>
                                <Add style={{ fontSize: 16, marginRight: 8, color: '#5aa5ef' }} />
                                <span style={{ fontSize: 13, fontFamily: 'Montserrat', fontWeight: 600 }}>Add New Subcategory</span>
                              </div>
                            </MenuItem>
                            <Divider style={{ margin: '4px 0', position: 'sticky', top: 36, backgroundColor: 'white', zIndex: 1 }} />
                            <MenuItem value="">
                              <span style={{ fontSize: 13, fontFamily: 'Montserrat', color: '#9ca3af' }}>None</span>
                            </MenuItem>
                            {subCategories.map((subcat) => (
                              <MenuItem key={subcat.id} value={subcat.id}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                  <Category style={{ fontSize: 15, marginRight: 8, color: '#5aa5ef' }} />
                                  <span style={{ fontSize: 13, fontFamily: 'Montserrat' }}>{subcat.title}</span>
                                </div>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>

                      {/* Playlist */}
                      <div>
                        <label
                          style={{
                            display: 'block',
                            marginBottom: 8,
                            fontSize: 13,
                            fontWeight: 700,
                            color: '#1f2937',
                            fontFamily: 'Montserrat',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          Playlists
                        </label>
                        <FormControl fullWidth variant="outlined" size="small">
                          <Select
                            multiple
                            value={selectedPlaylists}
                            onChange={(e) => {
                              const { value } = e.target;
                              // Check if "Create New" was clicked
                              if (value.includes('__create_new__')) {
                                setShowAddPlaylistModal(true);
                                return;
                              }
                              setSelectedPlaylists(value);
                            }}
                            style={{
                              backgroundColor: '#fafbfc',
                              borderRadius: 10,
                              fontFamily: 'Montserrat',
                              fontSize: 13,
                            }}
                            renderValue={(selected) => {
                              if (selected.length === 0) {
                                return <em style={{ color: '#9CA3AF' }}>Select playlists...</em>;
                              }
                              return `${selected.length} playlist${selected.length !== 1 ? 's' : ''} selected`;
                            }}
                            displayEmpty
                            MenuProps={{
                              variant: 'menu',
                              PaperProps: {
                                style: {
                                  maxHeight: 300,
                                },
                              },
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left',
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left',
                              },
                              getContentAnchorEl: null,
                            }}
                          >
                            <MenuItem
                              value="__create_new__"
                              style={{
                                position: 'sticky',
                                top: 0,
                                backgroundColor: 'white',
                                zIndex: 1,
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', color: '#5aa5ef' }}>
                                <Add style={{ fontSize: 16, marginRight: 8, color: '#5aa5ef' }} />
                                <span style={{ fontSize: 13, fontFamily: 'Montserrat', fontWeight: 600 }}>Add New Playlist</span>
                              </div>
                            </MenuItem>
                            <Divider style={{ margin: '4px 0', position: 'sticky', top: 36, backgroundColor: 'white', zIndex: 1 }} />
                            {playlists.length === 0 ? (
                              <MenuItem disabled>
                                <em style={{ color: '#9CA3AF' }}>No playlists available</em>
                              </MenuItem>
                            ) : (
                              playlists.map((playlist) => (
                                <MenuItem key={playlist.id} value={playlist.id}>
                                  <Checkbox
                                    checked={selectedPlaylists.includes(playlist.id)}
                                    style={{ color: '#5aa5ef' }}
                                  />
                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <PlaylistPlay style={{ fontSize: 15, marginRight: 8, color: '#5aa5ef' }} />
                                    <span
                                      style={{
                                        fontFamily: 'Montserrat',
                                        fontSize: 13,
                                        color: '#1f2937',
                                        fontWeight: 500,
                                      }}
                                    >
                                      {playlist.title}
                                    </span>
                                  </div>
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      </div>
                    </div>
                    </div>
                  </Collapse>
                </div>

                {/* Save Button - Fixed at bottom of left panel */}
                <div style={{ marginTop: 'auto', paddingTop: 20 }}>
                  <Button
                    onClick={handleSave}
                    disabled={
                      !title.trim()
                      || !selectedCategory.value
                      || isSaving
                      || (visibility === '3' && (!selectedGroups || selectedGroups.length === 0))
                    }
                    fullWidth
                    variant="contained"
                    style={{
                      backgroundColor:
                        title.trim() && selectedCategory.value && !isSaving
                        && !(visibility === '3' && (!selectedGroups || selectedGroups.length === 0))
                          ? '#5aa5ef'
                          : '#d1d5db',
                      color: 'white',
                      borderRadius: 10,
                      padding: '12px',
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: 'Montserrat',
                      boxShadow: 'none',
                      textTransform: 'none',
                    }}
                  >
                    {isSaving ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                        <CircularProgress size={18} style={{ color: 'white' }} />
                        <span>Saving...</span>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                        <Save style={{ fontSize: 20 }} />
                        <span>Save Changes</span>
                      </div>
                    )}
                  </Button>
                </div>
              </div>

              {/* Right Side: Thumbnail Preview - 55% */}
              <div
                style={{
                  flex: '0 0 55%',
                  backgroundColor: '#f9fafb',
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  position: 'relative',
                }}
              >
                {/* Main Video Preview */}
                <div
                  style={{
                    width: '100%',
                    maxWidth: '520px',
                    aspectRatio: '16/9',
                    backgroundColor: '#000',
                    borderRadius: 12,
                    overflow: 'hidden',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  {isVideoPlaying && currentVideoUrl && !videoFailed ? (
                    <ReactPlayer
                      key={currentVideoUrl}
                      url={currentVideoUrl}
                      controls
                      playing
                      muted
                      width="100%"
                      height="100%"
                      style={{ position: 'absolute', top: 0, left: 0 }}
                      onReady={handleVideoReady}
                      onError={handleVideoError}
                      config={{
                        file: {
                          forceVideo: true,
                          attributes: {
                            controlsList: 'nodownload',
                          },
                        },
                      }}
                    />
                  ) : videoFailed ? (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f5f5f5',
                      }}
                    >
                      <span style={{ color: '#888', fontSize: 14 }}>No preview available</span>
                    </div>
                  ) : (isCustomThumbnailSelected && customThumbnail) || thumbnails.length > 0 ? (
                    <>
                      <img
                        src={isCustomThumbnailSelected && customThumbnail ? customThumbnail : thumbnails[selectedThumbnail]}
                        alt="Selected thumbnail"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                      />
                      {currentVideoUrl && (
                        <button
                          type="button"
                          aria-label="Play video"
                          onClick={() => setIsVideoPlaying(true)}
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                          }}
                        >
                          <PlayArrow style={{ fontSize: 40, color: '#fff' }} />
                        </button>
                      )}
                    </>
                  ) : null}
                </div>

                {/* Video Info Badges */}
                {/* {videoData && (
                  <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 16px',
                        backgroundColor: '#fff',
                        borderRadius: 8,
                        border: '1px solid #e5e7eb',
                      }}
                    >
                        <span style={{ fontSize: 16 }}>⏱️</span>
                        {console.log('videoSize', videoSize, videoDuration)}
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: '#374151',
                          fontFamily: 'Montserrat',
                        }}
                      >
                        {videoDuration}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 16px',
                        backgroundColor: '#fff',
                        borderRadius: 8,
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      <span style={{ fontSize: 16 }}>📁</span>
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: '#374151',
                          fontFamily: 'Montserrat',
                        }}
                      >
                        {videoSize}
                      </span>
                    </div>
                  </div>
                )} */}

                {/* Thumbnail Selection Grid */}
                {thumbnails.length > 0 && (
                  <div style={{ marginTop: 24, width: '100%', maxWidth: '520px' }}>
                    <h3
                      style={{
                        color: '#6b7280',
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: 'Montserrat',
                        margin: '0 0 12px 0',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Choose Thumbnail
                    </h3>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: 8,
                      }}
                    >
                      {/* Custom Thumbnail Upload Option */}
                      <div
                        onClick={() => {
                          if (customThumbnail) {
                            setIsCustomThumbnailSelected(true);
                            setSelectedThumbnail(null);
                            setIsVideoPlaying(false);
                          } else {
                            customThumbnailInputRef.current?.click();
                          }
                        }}
                        style={{
                          position: 'relative',
                          cursor: 'pointer',
                          borderRadius: 6,
                          overflow: 'hidden',
                          border: isCustomThumbnailSelected
                            ? '2px solid #5aa5ef'
                            : '2px dashed #d1d5db',
                          aspectRatio: '16/9',
                          transition: 'all 0.2s ease',
                          boxShadow: isCustomThumbnailSelected
                            ? '0 0 0 3px rgba(90, 165, 239, 0.1)'
                            : 'none',
                          backgroundColor: customThumbnail ? 'transparent' : '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {customThumbnail ? (
                          <>
                            <img
                              src={customThumbnail}
                              alt="Custom thumbnail"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                            {isCustomThumbnailSelected && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  width: 20,
                                  height: 20,
                                  backgroundColor: '#5aa5ef',
                                  borderRadius: '50%',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                }}
                              >
                                <CheckCircle
                                  style={{ color: 'white', fontSize: 14 }}
                                />
                              </div>
                            )}
                          </>
                        ) : (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 4,
                              padding: 6,
                            }}
                          >
                            <CloudUpload
                              style={{
                                color: '#9ca3af',
                                fontSize: 18,
                              }}
                            />
                            <span
                              style={{
                                color: '#6b7280',
                                fontSize: 9,
                                fontFamily: 'Montserrat',
                                fontWeight: 600,
                                textAlign: 'center',
                              }}
                            >
                              Custom
                            </span>
                          </div>
                        )}
                      </div>
                      <input
                        ref={customThumbnailInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleCustomThumbnailUpload}
                        style={{ display: 'none' }}
                      />
                      {/* Existing Thumbnails */}
                      {thumbnails?.map((thumb, index) => (
                        <div
                          key={index}
                          onClick={() => {
                            setSelectedThumbnail(index);
                            setIsCustomThumbnailSelected(false);
                            setIsVideoPlaying(false);
                          }}
                          style={{
                            position: 'relative',
                            cursor: 'pointer',
                            borderRadius: 6,
                            overflow: 'hidden',
                            border:
                              selectedThumbnail === index && !isCustomThumbnailSelected
                                ? '2px solid #5aa5ef'
                                : '1px solid #e5e7eb',
                            aspectRatio: '16/9',
                            transition: 'all 0.2s ease',
                            boxShadow:
                              selectedThumbnail === index && !isCustomThumbnailSelected
                                ? '0 0 0 3px rgba(90, 165, 239, 0.1)'
                                : 'none',
                          }}
                        >
                          <img
                            src={thumb}
                            alt={`Thumbnail ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          {selectedThumbnail === index && !isCustomThumbnailSelected && (
                            <div
                              style={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                width: 20,
                                height: 20,
                                backgroundColor: '#5aa5ef',
                                borderRadius: '50%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                              }}
                            >
                              <CheckCircle
                                style={{ color: 'white', fontSize: 14 }}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </DialogContent>

      {/* Create Category Modal */}
      {showCreateCategoryModal && (
        <CreateCategoryPage
          initialOpen={showCreateCategoryModal}
          onModalClose={() => setShowCreateCategoryModal(false)}
          onCategoryCreated={handleCategoryCreated}
        />
      )}
    </Dialog>
  );
};

export default VideoEditModal;
